import { supabase, checkSupabaseConfig, isDemoMode } from './supabase'
import { AUTH_CONFIG } from './config'
import type {
  AuthResult,
  AuthError,
  SignUpData,
  SignInData,
  PasswordResetData,
  ProfileUpdateData,
  CraftyJoyUser,
  OAuthProvider,
  OAuthConfig,
  UserProfile,
  ProjectCreateData,
  ProjectUpdateData,
  ProgressUpdateData,
  RelationshipCreateData,
  AchievementCreateData,
} from './types/auth'
import type { Session, User } from '@supabase/supabase-js'

export class SupabaseAuthService {
  private static instance: SupabaseAuthService
  private initialized = false

  private constructor() {
    this.initialize()
  }

  public static getInstance(): SupabaseAuthService {
    if (!SupabaseAuthService.instance) {
      SupabaseAuthService.instance = new SupabaseAuthService()
    }
    return SupabaseAuthService.instance
  }

  private async initialize(): Promise<void> {
    if (this.initialized) return

    try {
      // Check if Supabase is properly configured
      const config = checkSupabaseConfig()
      
      // In production, if Supabase is not configured, enable demo mode
      if (!config.isValid) {
        console.warn('Supabase configuration issues detected:', config.errors)
        
        if (import.meta.env.PROD) {
          console.warn('Running in production without Supabase configuration - enabling demo mode')
          // Set demo mode flag in environment
          (window as any).__CRAFTYJOY_DEMO_MODE__ = true
          this.initialized = true
          return
        } else if (!isDemoMode()) {
          console.error('Supabase configuration errors:', config.errors)
          throw new Error('Supabase is not properly configured')
        }
      }

      // Set up auth state change listener only if Supabase is properly configured
      if (config.isValid) {
        supabase.auth.onAuthStateChange(async (event, session) => {
          console.log('Auth state changed:', event, session?.user?.id)
          
          if (event === 'SIGNED_IN' && session?.user) {
            await this.updateLastActiveTime(session.user.id)
          }
          
          if (event === 'SIGNED_OUT') {
            this.clearLocalData()
          }
        })
      }

      this.initialized = true
    } catch (error) {
      console.error('Failed to initialize SupabaseAuthService:', error)
      
      // In production, fall back to demo mode instead of throwing
      if (import.meta.env.PROD) {
        console.warn('Falling back to demo mode due to initialization error')
        ;(window as any).__CRAFTYJOY_DEMO_MODE__ = true
        this.initialized = true
        return
      }
      
      throw error
    }
  }

  // Authentication Methods

  async signUp(data: SignUpData): Promise<AuthResult> {
    try {
      const { email, password, username, displayName, role, ageGroup } = data

      // Validate input
      this.validateSignUpData(data)

      const { data: authData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            display_name: displayName || username || email.split('@')[0],
            role: role || 'student',
            age_group: ageGroup || '9-11',
          },
        },
      })

      if (error) {
        return {
          user: null,
          session: null,
          error: this.mapSupabaseError(error),
        }
      }

      // Get the user with profile data
      let user: CraftyJoyUser | null = authData.user
      if (user) {
        const profile = await this.getUserProfile(user.id)
        user = { ...user, profile }
      }

      return {
        user,
        session: authData.session,
        error: null,
      }
    } catch (error) {
      return {
        user: null,
        session: null,
        error: this.handleError(error),
      }
    }
  }

  async signIn(data: SignInData): Promise<AuthResult> {
    try {
      const { email, password } = data

      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        return {
          user: null,
          session: null,
          error: this.mapSupabaseError(error),
        }
      }

      // Get the user with profile data
      let user: CraftyJoyUser | null = authData.user
      if (user) {
        const profile = await this.getUserProfile(user.id)
        user = { ...user, profile }
      }

      return {
        user,
        session: authData.session,
        error: null,
      }
    } catch (error) {
      return {
        user: null,
        session: null,
        error: this.handleError(error),
      }
    }
  }

  async signInWithOAuth(config: OAuthConfig): Promise<AuthResult> {
    try {
      const { provider, options } = config

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: options?.redirectTo || AUTH_CONFIG.getRedirectUrl('/auth/callback'),
          scopes: options?.scopes,
          queryParams: options?.queryParams,
        },
      })

      if (error) {
        return {
          user: null,
          session: null,
          error: this.mapSupabaseError(error),
        }
      }

      // OAuth returns a URL, not user data immediately
      return {
        user: null,
        session: null,
        error: null,
      }
    } catch (error) {
      return {
        user: null,
        session: null,
        error: this.handleError(error),
      }
    }
  }

  async signOut(): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        return { error: this.mapSupabaseError(error) }
      }

      this.clearLocalData()
      return { error: null }
    } catch (error) {
      return { error: this.handleError(error) }
    }
  }

  async resetPassword(data: PasswordResetData): Promise<{ error: AuthError | null }> {
    try {
      const { email } = data

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })

      if (error) {
        return { error: this.mapSupabaseError(error) }
      }

      return { error: null }
    } catch (error) {
      return { error: this.handleError(error) }
    }
  }

  async updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) {
        return { error: this.mapSupabaseError(error) }
      }

      return { error: null }
    } catch (error) {
      return { error: this.handleError(error) }
    }
  }

  async getSession(): Promise<Session | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      return session
    } catch (error) {
      console.error('Error getting session:', error)
      return null
    }
  }

  async getCurrentUser(): Promise<CraftyJoyUser | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) return null

      const profile = await this.getUserProfile(user.id)
      return { ...user, profile }
    } catch (error) {
      console.error('Error getting current user:', error)
      return null
    }
  }

  async refreshSession(): Promise<Session | null> {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession()
      
      if (error) {
        console.error('Error refreshing session:', error)
        return null
      }

      return session
    } catch (error) {
      console.error('Error refreshing session:', error)
      return null
    }
  }

  // Profile Management

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error getting user profile:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error getting user profile:', error)
      return null
    }
  }

  async updateProfile(userId: string, updates: ProfileUpdateData): Promise<{ error: AuthError | null }> {
    try {
      const profileUpdates: any = {}

      if (updates.username) profileUpdates.username = updates.username
      if (updates.displayName) profileUpdates.display_name = updates.displayName
      if (updates.bio) profileUpdates.bio = updates.bio
      if (updates.avatarUrl) profileUpdates.avatar_url = updates.avatarUrl
      if (updates.learningGoals) profileUpdates.learning_goals = updates.learningGoals
      if (updates.preferences) profileUpdates.preferences = updates.preferences

      const { error } = await supabase
        .from('user_profiles')
        .update(profileUpdates)
        .eq('id', userId)

      if (error) {
        return { error: this.mapSupabaseError(error) }
      }

      return { error: null }
    } catch (error) {
      return { error: this.handleError(error) }
    }
  }

  // Progress Management

  async updateProgress(userId: string, progress: ProgressUpdateData): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase
        .from('user_progress')
        .upsert({
          user_id: userId,
          module_id: progress.moduleId,
          concept_id: progress.conceptId,
          progress_percentage: progress.progressPercentage,
          time_spent_minutes: progress.timeSpentMinutes,
          metadata: progress.metadata,
          last_accessed_at: new Date().toISOString(),
        })

      if (error) {
        return { error: this.mapSupabaseError(error) }
      }

      return { error: null }
    } catch (error) {
      return { error: this.handleError(error) }
    }
  }

  // Utility Methods

  private async updateLastActiveTime(userId: string): Promise<void> {
    try {
      await supabase
        .from('user_profiles')
        .update({ last_active_at: new Date().toISOString() })
        .eq('id', userId)
    } catch (error) {
      console.warn('Failed to update last active time:', error)
    }
  }

  private clearLocalData(): void {
    try {
      // Clear any cached user data
      localStorage.removeItem('craftyjoy_user_cache')
      localStorage.removeItem('craftyjoy_demo_user')
    } catch (error) {
      console.warn('Failed to clear local data:', error)
    }
  }

  private validateSignUpData(data: SignUpData): void {
    if (!data.email || !data.password) {
      throw new Error('Email and password are required')
    }

    if (data.password.length < 8) {
      throw new Error('Password must be at least 8 characters long')
    }

    if (data.username && data.username.length < 3) {
      throw new Error('Username must be at least 3 characters long')
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(data.email)) {
      throw new Error('Please enter a valid email address')
    }
  }

  private mapSupabaseError(error: any): AuthError {
    const errorMap: Record<string, string> = {
      'Invalid login credentials': 'Invalid email or password',
      'Email not confirmed': 'Please check your email and click the confirmation link',
      'User already registered': 'An account with this email already exists',
      'Password should be at least 6 characters': 'Password must be at least 6 characters long',
      'Signup requires a valid password': 'Please enter a valid password',
    }

    return {
      message: errorMap[error.message] || error.message || 'An unexpected error occurred',
      code: error.status?.toString(),
      status: error.status,
    }
  }

  private handleError(error: any): AuthError {
    console.error('Auth service error:', error)
    
    return {
      message: error.message || 'An unexpected error occurred',
      code: error.code,
      status: error.status,
    }
  }

  // Development/Demo helpers

  isDemoMode(): boolean {
    return isDemoMode()
  }

  getConfigStatus() {
    return checkSupabaseConfig()
  }
}

// Create and export singleton instance
export const authService = SupabaseAuthService.getInstance()